/**
 * Warehouse Details
 */

// React
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
// Redux
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Store } from '../../../redux/reducers';
import {
  getSkuInventoryWithIdAction,
  getSkusInventoryFinalizedSumActions,
  getSkusInventoryNotFinalizedSumActions,
} from '../../warehouses/actions';

// Selector
import { createStructuredSelector } from 'reselect';
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectSkusInventory,
  makeSelectSkusInventoryCount,
  makeSelectAvailableCount,
  makeSelectOnWayCount,
  makeSelectReturnedCount,
} from '../../warehouses/selectors';

//UI
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Grid,
  Button,
  TextField,
  Divider,
  Tooltip,
  TooltipProps,
  FormHelperText,
  MenuItem,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiTruckDeliveryOutline,
  mdiClose,
  mdiBookshelf,
  mdiCashMultiple,
  mdiHelpCircleOutline,
} from '@mdi/js';

// Type
import { SelectorPropsSkusInventoryDetails } from '../interfaces';
import useQuery from '../../../utils/hook/useQuery';
import theme from '../../../theme';
import warehouseRepository from 'client-sdk/src/Repository/warehouses-repository';
import handleNotfication from '../../../utils/handleNotfication';
import handleError from '../../../utils/handleError';

type Inputs = {
  'name.contains': string;
};

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& > *': {
        '@media (max-width: 768px)': {
          margin: theme.spacing(0),
        },
      },
      '& #showFilter .MuiInput-underline::before, & #showFilter .MuiInput-underline::after': {
        borderBottom: '2px solid #DFA01F !important',
      },
    },
    paper: {
      marginTop: '36px',
      marginBottom: '36px',
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      border: '1px solid #ddd',
      borderRadius: '4px',
      '@media (max-width: 768px)': {
        padding: theme.spacing(0),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(3),
        backgroundColor: theme.palette.background.default,
      },
    },
    inventory: {
      display: 'flex',
      marginTop: '20px',
    },
    item: {
      display: 'flex',
      alignSelf: 'center',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 500,
    },
    icon: {
      marginRight: theme.spacing(1),
    },
    divider: {
      width: '98%',
      margin: '10px auto',
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 400,
      color: '#8A8A8A',
      marginRight: '10px',
    },
    headerBox: {
      display: 'flex',
      alignItems: 'center',
    },
    showCount: {
      '& .MuiSelect-icon': {
        color: '#DFA01F',
      },
    },
    contentlabelTitle: {
      position: 'relative',
      color: theme.palette.gray.light,
      marginLeft: '40px',
      fontSize: '14px',
      fontWeight: 500,
      '&:before': {
        content: '"-"',
        position: 'absolute',
        left: '-20px',
      },
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'justify',
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      padding: '10px 0',
      borderRadius: '4px',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },

    input: {
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 8,
      position: 'relative',
      marginRight: '10px',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    returnButton: {
      margin: '40px 0 36px',
      padding: 0,
    },
    boxes: {
      marginBottom: '20px',
    },
    textFields: {
      '& label': {
        zIndex: 100,
      },
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      top: '47px',
      right: '0',
      marginTop: '12px',
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },
    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    box: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f6f6f6',
      padding: theme.spacing(3, 4),
      borderRadius: '4px',
      border: '1px solid #dfdfdf',
      margin: '0 15px',
      height: '100%',
    },
    boxIcon: {
      marginRight: '30px',
    },
    allCenter: {
      position: 'relative',
      textAlign: 'center',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '60px',
        height: '60px',
        top: '47%',
        left: '40%',
        transform: 'translate(-50%,-50%)',
        borderRadius: '50%',
        opacity: '0.3',
        backgroundColor: '#696969',
      },
    },
    activeCenter: {
      position: 'relative',
      textAlign: 'center',

      '&:before': {
        content: '""',
        position: 'absolute',
        width: '60px',
        height: '60px',
        top: '47%',
        left: '40%',
        transform: 'translate(-50%,-50%)',
        borderRadius: '50%',
        opacity: '0.3',
        backgroundColor: '#039590',
      },
    },
    diactiveCenter: {
      position: 'relative',
      textAlign: 'center',

      '&:before': {
        content: '""',
        position: 'absolute',
        width: '60px',
        height: '60px',
        top: '47%',
        left: '40%',
        transform: 'translate(-50%,-50%)',
        borderRadius: '50%',
        opacity: '0.3',
        backgroundColor: '#fd8686',
      },
    },

    title: {
      textAlign: 'center',
      color: '#8A8A8A',
      fontSize: '16px',
      fontWeight: 500,
      marginRight: '8px',
    },
    text: {
      textAlign: 'center',
      fontSize: '35px',
      fontWeight: 500,
      padding: '0 30px',
      marginBottom: '10px',
    },
  })
);

type Props = {
  id: number;
  selectedItems: number;
  selectedItemsDetail: any;
  handleChangeInventorySetting: () => void;
};

export function SkuInventoryDetails(props: Props) {
  const classes = useStyles();
  const { register, handleSubmit, errors, control, getValues, watch } = useForm({
    // defaultValues: {
    //   warehouse: 4,
    // },
  });
  const [inventory, setInventory] = useState({} as any);
  const [suppliable, setSuppliable] = useState('');
  const [reserved, setReserved] = useState('');
  const [minimum, setMinimum] = useState('');
  const [WarehouseInventoryLoading, setWarehouseInventoryLoading] = useState(
    false as boolean
  );
  const [WarehouseInventoryError, setWarehouseInventoryError] = useState({});
  const [warehouseOptions, setWarehouseOptions] = useState<any>([]);
  const { filtersValues, setFiltersValues } = useQuery({
    page: 0,
    size: 10,
  } as any);

  const warehouseInventory = async (actions: any) => {
    const payload = { ...actions };
    try {
      setWarehouseInventoryLoading(true);
      const response = await warehouseRepository.getWarehouseInventory({
        payload,
      });
      const result = response.data[0];
      setInventory(result);
      setMinimum(result.minimum);
      setReserved(result.reserved);
      setSuppliable(result.suppliable);
    } catch (error) {
      setWarehouseInventoryError(error);
    } finally {
      setWarehouseInventoryLoading(false);
    }
  };
  const fetchWarehouses = async () => {
    const payload = {
      'status.equals': 'ACTIVE',
      size: 100,
    };
    try {
      const { response, count } = await warehouseRepository.getWarehouses({
        payload,
      });
      setWarehouseOptions(response.data);
    } catch (err) {
      //
    }
  };
  useEffect(() => {
    const warehouseId = getValues('warehouse');
    props.selectedItemsDetail.length === 1 &&
      warehouseInventory({
        variantId: { equals: props.selectedItemsDetail[0].id },
        warehouseId: { equals: warehouseId },
      });
    fetchWarehouses();
  }, [props.id, watch('warehouse')]);

  const submitForm = () => {
    const warehouseId = getValues('warehouse');
    const partnerId = localStorage.getItem('partnerId');
    // const warehouse = warehouseOptions.filter((warehouseOption)=> warehouseOption.name ===warehouseName );
    const ids = props.selectedItemsDetail.map((item: any) => {
      return {
        variantId: item.id,
        warehouseId: warehouseId,
        partnerId: parseInt(partnerId),
      };
    });
    const data =
      ids.length > 1
        ? { variantWarehouseKeys: ids, suppliable, reserved, minimum }
        : { ...ids[0], suppliable, reserved, minimum };
    if (ids.length > 1) {
      updateInventories({ body: data });
      return;
    }
    updateInventory({ body: data });
  };

  const updateInventories = async (actions: any) => {
    const payload = { ...actions };
    try {
      setWarehouseInventoryLoading(true);
      const response = await warehouseRepository.updateWarehouseInventories({
        payload,
      });
      handleNotfication('موجودی انبار با موفقیت تغییر یافت', 'success');
      props.handleChangeInventorySetting();
    } catch (error) {
      setWarehouseInventoryError(error);
      handleError(error);
    } finally {
      setWarehouseInventoryLoading(false);
    }
  };

  const updateInventory = async (actions: any) => {
    const payload = { ...actions };
    try {
      setWarehouseInventoryLoading(true);
      const response = await warehouseRepository.updateWarehouseInventory({
        payload,
      });
      handleNotfication('موجودی انبار با موفقیت تغییر یافت', 'success');
      props.handleChangeInventorySetting();
    } catch (error) {
      setWarehouseInventoryError(error);
      handleError(error);
    } finally {
      setWarehouseInventoryLoading(false);
    }
  };

  const useStylesBootstrap = makeStyles((theme: Theme) => ({
    arrow: {
      color: theme.palette.common.black,
    },
    tooltip: {
      backgroundColor: theme.palette.common.black,
    },
  }));

  function BootstrapTooltip(props: TooltipProps) {
    const classes = useStylesBootstrap();

    return <Tooltip arrow classes={classes} {...props} />;
  }

  return (
    <div className={classes.root}>
      {props.selectedItems <= 1 && (
        <Grid
          style={{ marginTop: '10px', marginBottom: '10px' }}
          className={classes.boxes}
          container
        >
          <Grid item xs={4}>
            <div className={classes.box}>
              <div className={classes.text}>{inventory?.located}</div>
              <div className={classes.title}>موجودی شلف</div>
            </div>
          </Grid>
          <Grid item xs={4}>
            <div className={classes.box}>
              <div className={classes.text}>{inventory?.orderable}</div>
              <div className={classes.title}>موجودی قابل سفارش</div>
            </div>
          </Grid>

          <Grid item xs={4}>
            <div className={classes.box}>
              <div className={classes.text}>{inventory?.available}</div>
              <div className={classes.title}>موجودی در دسترس</div>
            </div>
          </Grid>
        </Grid>
      )}
      <Grid
        style={{ marginTop: '20px', marginBottom: '10px' }}
        container
        spacing={1}
      >
        <Grid container item xs={12} sm={4}>
          <span style={{ width: '100%', margin: '8px 0' }}>
            <Controller
              style={{ width: '100%' }}
              control={control}
              name="warehouse"
              label="انبار"
              rules={{ required: true }}
              as={
                <TextField
                  size="small"
                  select
                  fullWidth
                  variant="outlined"
                  error={errors.warehouse && true}
                >
                  {warehouseOptions?.length > 0 ? (
                    warehouseOptions.map((item: any, index: number) => (
                      <MenuItem
                        data-cy={'start' + index}
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">در حال بارگزاری</MenuItem>
                  )}
                </TextField>
              }
            />
            <FormHelperText error focused>
              {errors.warehouse && 'مبدا سفارش الزامی میباشد'}
            </FormHelperText>
          </span>
        </Grid>
        <Grid container item xs={12} sm={4}>
          <span style={{ width: '100%', margin: '8px 0' }}>
            <TextField
              fullWidth
              className={classes.textFields}
              variant="outlined"
              id="available"
              name="available"
              size="small"
              inputRef={register({
                required: true,
              })}
              value={minimum}
              onChange={e => setMinimum(e.target.value)}
              label={
                <Grid container alignItems="center">
                  <span style={{ marginLeft: '5px' }}>{'نقطه سفارش مجدد'}</span>
                  <BootstrapTooltip title="با تنظیم عدد برای این فیلد، زمانی که موجودی کالای شما به این عدد برسد، برای یادآوری جهت تامین کالا پیامی به شما نمایش داده خواهد شد">
                    <Icon
                      path={mdiHelpCircleOutline}
                      size={0.7}
                      color={theme.palette.gray.main}
                    />
                  </BootstrapTooltip>
                </Grid>
              }
            />
          </span>
        </Grid>
        <Grid item container xs={12} sm={4}>
          <span style={{ width: '100%', margin: '8px 0' }}>
            <TextField
              fullWidth
              id="available"
              className={classes.textFields}
              name="available"
              size="small"
              variant="outlined"
              inputRef={register({
                required: true,
              })}
              value={reserved}
              onChange={e => setReserved(e.target.value)}
              label={
                <Grid container alignItems="center">
                  <span style={{ marginLeft: '5px' }}>{'موجودی رزرو'}</span>
                  <BootstrapTooltip title="امکان ثبت سفارش برای تعداد کالایی که در موجودی رزرو ثبت شده اند وجود ندارد">
                    <Icon
                      path={mdiHelpCircleOutline}
                      size={0.7}
                      color={theme.palette.gray.main}
                    />
                  </BootstrapTooltip>
                </Grid>
              }
            />
          </span>
        </Grid>
        <Grid item container xs={12} sm={4}>
          <span style={{ width: '100%', margin: '8px 0' }}>
            <TextField
              fullWidth
              className={classes.textFields}
              id="available"
              name="available"
              size="small"
              variant="outlined"
              inputRef={register({
                required: true,
              })}
              value={suppliable}
              onChange={e => setSuppliable(e.target.value)}
              label={
                <Grid container alignItems="center">
                  <span style={{ marginLeft: '5px' }}>{'موجودی قابل تامین'}</span>
                  <BootstrapTooltip title="در صورتی که موجودی شما برای سفارش کافی نباشد، به تعدادی که در این قسمت مشخص کرده اید سفارش ثبت می شود اما تا زمانی که کالا توسط شما تامین نشود، سفارش پردازش نمی شود">
                    <Icon
                      path={mdiHelpCircleOutline}
                      size={0.7}
                      color={theme.palette.gray.main}
                    />
                  </BootstrapTooltip>
                </Grid>
              }
            />
          </span>
        </Grid>
      </Grid>
      <Divider />
      <Grid
        style={{ marginTop: '10px', marginBottom: '10px' }}
        container
        justify="flex-end"
      >
        <Button
          variant="contained"
          color="primary"
          size="medium"
          onClick={submitForm}
          style={{ display: 'flex', flex: '1 1 0' }}
        >
          {'ذخیره تغییرات'}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          onClick={props.close}
          style={{ display: 'flex', flex: '1 1 0', marginRight: '8px' }}
        >
          {'انصراف'}
        </Button>
      </Grid>
    </div>
  );
}

const mapStateToProps = createStructuredSelector<
  Store,
  SelectorPropsSkusInventoryDetails
>({
  skuInventory: makeSelectSkusInventory(),
  skuInventoryCount: makeSelectSkusInventoryCount(),
  availableCount: makeSelectAvailableCount(),
  onWayCount: makeSelectOnWayCount(),
  returnedCount: makeSelectReturnedCount(),
  loading: makeSelectLoading(),
  error: makeSelectError(),
});

function mapDispatchToProps(dispatch: Function) {
  return {
    getSkusInventoryWithIdDispatch: (id: number, pagination: object) =>
      dispatch(getSkuInventoryWithIdAction({ id, pagination })),
    getSkusInventoryFinalizedSumDispatch: (id: number, pagination: object) =>
      dispatch(getSkusInventoryFinalizedSumActions({ id, pagination })),
    getSkusInventoryNotFinalizedSumDispatch: (id: number, pagination: object) =>
      dispatch(getSkusInventoryNotFinalizedSumActions({ id, pagination })),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect)(SkuInventoryDetails);
